import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'

import { initializeApp } from 'firebase/app'
import { connectAuthEmulator, getAuth } from 'firebase/auth'
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
} from 'firebase/firestore'
import { getAnalytics } from 'firebase/analytics'

import { getRemoteConfig, isSupported } from 'firebase/remote-config'
import { REMOTE_CONFIG_DEFAULTS } from '../lib/featureFlagKeys'

import { connectStorageEmulator, getStorage } from 'firebase/storage'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import { logger } from '../lib/logger'

export const functionsRegion = 'europe-west3'

export async function initFirebase() {
  let firebaseApp, functions, firestore, auth, storage, remoteConfig, analytics

  if (import.meta.env.MODE === 'development') {
    firebaseApp = initializeApp({
      apiKey: 'local-api-key',
      projectId: 'cleverlohn-local',
      authDomain: 'localhost:3000',
      storageBucket: 'cleverlohn-local.appspot.com',
    })

    // Create listener logic
    window.emulatorsEvaluated = false
    window.emulatorsEvaluatedListeners = []
    window.onEmulatorsEvaluated = (listener) => {
      if (window.emulatorsEvaluated) {
        listener({ firestore, functions, auth, storage })
      } else {
        window.emulatorsEvaluatedListeners.push(listener)
      }
    }
    ;(async () => {
      try {
        const authEmulatorUrl =
          import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST ||
          'http://localhost:9099'
        const firestoreHost =
          import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || 'localhost'
        const firestorePort = Number(
          import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080
        )
        const storageHost =
          import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_HOST || 'localhost'
        const storagePort = Number(
          import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_PORT || 9199
        )
        const functionsHost = 'localhost'
        const functionsPort = Number(
          import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || 5001
        )

        await fetch(authEmulatorUrl)

        firestore = getFirestore(firebaseApp)
        functions = getFunctions(firebaseApp, functionsRegion)
        auth = getAuth(firebaseApp)
        auth.languageCode = 'de'
        storage = getStorage(firebaseApp)

        connectFunctionsEmulator(functions, functionsHost, functionsPort)
        connectAuthEmulator(getAuth(firebaseApp), authEmulatorUrl)
        connectFirestoreEmulator(
          getFirestore(firebaseApp),
          firestoreHost,
          firestorePort
        )
        connectStorageEmulator(
          getStorage(firebaseApp),
          storageHost,
          storagePort
        )
      } catch (e) {
        logger.error('Could not connect to emulators', { error: e })
      }

      window.emulatorsEvaluated = true
      window.emulatorsEvaluatedListeners.forEach((listener) => {
        listener({ firestore, functions, auth, storage })
      })
    })()
  } else {
    // Your web app's Firebase configuration
    const firebaseConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    }
    firebaseApp = initializeApp(firebaseConfig)
    functions = getFunctions(firebaseApp, functionsRegion)
    firestore = initializeFirestore(firebaseApp, {
      experimentalAutoDetectLongPolling: true,
      // Safari/iOS 26.4+ WebKit holds Firestore fetch-stream frames in an
      // internal buffer, which we believe corrupts watch-change parsing
      // (`FirebaseError: incomplete key.`, CL-WEBAPP-GH) and crashes the app
      // into the ErrorBoundary reload on the employee documents page. Fall
      // back to the pre-v9 XHR transport, the workaround recommended by
      // Firebase in https://github.com/firebase/firebase-js-sdk/issues/9789.
      // Remove once that issue is fixed upstream and the SDK is bumped.
      // CL-1876.
      useFetchStreams: false,
    })
    auth = getAuth(firebaseApp)
    auth.languageCode = 'de'
    storage = getStorage(firebaseApp)
    analytics = getAnalytics(firebaseApp)

    // Firebase App Check - ALWAYS enabled for bot protection
    // Debug token support: If self.FIREBASE_APPCHECK_DEBUG_TOKEN is set (by Playwright),
    // Firebase SDK will use the debug token instead of reCAPTCHA
    if (typeof self !== 'undefined' && self.FIREBASE_APPCHECK_DEBUG_TOKEN) {
      // E2E tests: Playwright injects debug token before page loads
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = self.FIREBASE_APPCHECK_DEBUG_TOKEN
    } else if (import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN) {
      // Local dev with debug token from env var
      window.FIREBASE_APPCHECK_DEBUG_TOKEN =
        import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN
    } else if (
      // Aikido Pentest Agent - bypass App Check using debug token
      // SECURITY: Only enabled on staging to prevent accidental production bypass
      // Detection: User-Agent (fast path) OR IP address match (fallback)
      // IPs from: https://help.aikido.dev/pentests/ip-addresses-for-pentest
      import.meta.env.VITE_FIREBASE_PROJECT_ID === 'cleverlohn-staging' &&
      import.meta.env.VITE_AIKIDO_APPCHECK_DEBUG_TOKEN
    ) {
      let isAikidoPentest =
        typeof navigator !== 'undefined' &&
        navigator.userAgent?.includes('aikido-pentest')

      if (!isAikidoPentest) {
        // Aikido doesn't always set its UA on page navigations; fall back to IP detection
        const aikidoIPs = new Set([
          '52.48.122.82',
          '54.194.175.200',
          '52.210.210.125',
          '54.76.103.212',
          '54.217.255.121',
          '52.49.182.62',
          '34.252.102.184',
          '52.209.168.11',
          '79.127.239.171',
        ])
        try {
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 3000)
          const res = await fetch('https://api.ipify.org?format=json', {
            signal: controller.signal,
          })
          clearTimeout(timeoutId)
          const { ip } = await res.json()
          isAikidoPentest = aikidoIPs.has(ip)
        } catch (_e) {
          // Timeout or network error — skip IP-based detection
        }
      }

      if (isAikidoPentest) {
        window.FIREBASE_APPCHECK_DEBUG_TOKEN =
          import.meta.env.VITE_AIKIDO_APPCHECK_DEBUG_TOKEN
      }
    }

    // Initialize Firebase App Check with reCAPTCHA v3 provider
    // Firebase SDK automatically uses debug token if window.FIREBASE_APPCHECK_DEBUG_TOKEN is set
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(
        import.meta.env.VITE_RECAPTCHA_SITE_KEY
      ),
      isTokenAutoRefreshEnabled: true,
    })

    if (await isSupported()) {
      remoteConfig = getRemoteConfig(firebaseApp)
      // In-repo defaults so a gated feature stays off (per its default) before
      // the first Remote Config fetch completes or if a key is missing.
      remoteConfig.defaultConfig = REMOTE_CONFIG_DEFAULTS
      // Staging fetches on every load for instant flag flips. Other
      // environments use a 5 minute interval so flag changes propagate quickly
      // instead of sitting behind the SDK's 12 hour default.
      if (import.meta.env.MODE === 'staging') {
        remoteConfig.settings.minimumFetchIntervalMillis = 0
      } else {
        remoteConfig.settings.minimumFetchIntervalMillis = 5 * 60 * 1000
      }
    } else {
      remoteConfig = null
      logger.warn(
        'Firebase Remote Config is not supported in this browser; skipping initialization'
      )
    }
  }

  return {
    firebaseApp,
    functions,
    firestore,
    storage,
    auth,
    remoteConfig,
    analytics,
  }
}
